import { Helper } from '../helper';
import type { Question } from '../models/Question';
import type { Group } from '../models/Group';
import type { ValidationResponse } from '../types';
import type { FormService } from './formService';
import type { IValidationService } from './validationService';

export class ValidationService implements IValidationService {
  constructor(protected formService: FormService) {}

  public validate(question: Question, group: Group): ValidationResponse {
    let fieldResponse = this.validateRequired(question);

    if (!fieldResponse.valid) {
      return fieldResponse;
    }

    if (!question.hasPopulatedAnswer() && question.optional) {
      return this.createResponse(true);
    }

    fieldResponse = this.validateQuestionType(question);

    if (!fieldResponse.valid) {
      return fieldResponse;
    }

    return this.validateAnswer(question, group);
  }

  protected validateRequired(question: Question): ValidationResponse {
    if (!question.hasPopulatedAnswer() && !question.optional) {
      return this.createResponse(false, 'Required');
    }

    return this.createResponse(true);
  }

  protected validateQuestionType(question: Question): ValidationResponse {
    switch (question.type) {
      case 'Text':
        return this.validateTextType(question);
      case 'Date':
        return this.validateDateType(question);
      case 'DateText':
        return this.validateDateTextType(question);
      case 'Email':
        return this.validateEmailType(question);
      case 'Numeric':
        return this.validateNumericType(question);
      case 'Phone':
        return this.validatePhoneType(question);
      case 'MonthYear':
        return this.validateMonthYearType(question);
      default:
        return this.createResponse(true);
    }
  }

  protected validateAnswer(_question: Question, _group: Group): ValidationResponse {
    return this.createResponse(true);
  }

  protected createResponse(valid: boolean, message: string = ''): ValidationResponse {
    return {
      valid,
      message,
    };
  }

  protected validateNumericType(question: Question): ValidationResponse {
    if (isNaN(Number(question.answer))) {
      return this.createResponse(false, 'Please provide a numeric value.');
    }

    return this.createResponse(true);
  }

  protected validateTextType(question: Question): ValidationResponse {
    const maxLength = 100;

    if (question.hasPopulatedAnswer()) {
      if (String(question.answer).length > maxLength) {
        return this.createResponse(false, `Text has reached a maximum of ${maxLength} characters.`);
      }
    }

    return this.createResponse(true);
  }

  protected validateEmailType(question: Question): ValidationResponse {
    if (!this.validateEmail(String(question.answer))) {
      return this.createResponse(false, 'Please enter a valid email address.');
    }

    return this.createResponse(true);
  }

  protected validateDateTextType(question: Question): ValidationResponse {
    if (!Helper.validateDate(String(question.answer))) {
      return this.createResponse(false, 'Please enter a valid date (dd/mm/yyyy).');
    }

    return this.createResponse(true);
  }

  protected validateDateType(question: Question): ValidationResponse {
    if (!Helper.validateDate(String(question.answer))) {
      return this.createResponse(false, 'Please enter a valid date (dd/mm/yyyy).');
    }

    return this.createResponse(true);
  }

  protected validatePhoneType(question: Question): ValidationResponse {
    if (!Helper.validatePhone(String(question.answer))) {
      return this.createResponse(false, 'Numeric characters only.');
    }

    return this.createResponse(true);
  }

  protected validateMonthYearType(question: Question): ValidationResponse {
    if (!Helper.validateMonthYear(String(question.answer))) {
      return this.createResponse(false, 'Please enter a valid date (mm/yyyy).');
    }

    return this.createResponse(true);
  }

  protected validateEmail(email: string): boolean {
    return Helper.validateEmail(email);
  }
}
